import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth,
  User,
  signInWithEmailAndPassword,
  signOut,
  user,
} from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IServerResponse } from '../models/server-response.model';
import { IUser, UserModel } from '../models/user.model';

const MAX_ATTEMPTS = 3;

/**
 * Codigos donde reintentar no sirve de nada: el permiso no va a aparecer solo.
 * 401/403 = la cuenta no es `sudo`; 404 = el API no conoce al usuario.
 */
const NO_RETRY_STATUS = new Set([401, 403, 404]);

/**
 * Codigo del error, ya sea el HTTP de la respuesta o el `statusCode` del
 * envelope de keystoreman-console-api (OJO: es `statusCode`, no `status`).
 */
const statusOf = (error: unknown): number => {
  const envelope = (error as { error?: { statusCode?: number } })?.error?.statusCode;
  if (typeof envelope === 'number' && envelope > 0) return envelope;
  if (error instanceof HttpErrorResponse) return error.status;
  const status = (error as { status?: number })?.status;
  return typeof status === 'number' ? status : 0;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  /** Usuario de KeyStoreMan (GET /account), no el de Firebase. */
  readonly currentUser = signal<UserModel | null>(null);
  readonly loggedIn = computed(() => this.currentUser() !== null);
  readonly loading = signal(false);

  /** Espera a que Firebase resuelva el estado de sesion inicial. */
  waitForAuthReady(): Promise<User | null> {
    return firstValueFrom(user(this.auth));
  }

  async signIn(email: string, password: string): Promise<User> {
    const credential = await signInWithEmailAndPassword(this.auth, email, password);
    return credential.user;
  }

  /** ID token fresco de Firebase. Se manda CRUDO, sin prefijo `Bearer`. */
  async getToken(forceRefresh = false): Promise<string> {
    const firebaseUser = this.auth.currentUser ?? (await this.waitForAuthReady());
    if (!firebaseUser) throw new Error('No hay sesion activa.');
    return firebaseUser.getIdToken(forceRefresh);
  }

  /**
   * GET /account con hasta 3 reintentos y backoff de 1000 * intento ms.
   * El API tarda en reflejar al usuario recien creado en Firebase, de ahi el
   * reintento; pero solo para fallos de red y 5xx.
   *
   * Ante 401 / 403 / 404 corta de inmediato: un usuario sin perfil `sudo`
   * debe ver el mensaje al instante, no despues de ~3 s de espera inutil.
   */
  async getUser(): Promise<UserModel> {
    let lastError: unknown = null;
    this.loading.set(true);
    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await firstValueFrom(
            this.http.get<IServerResponse<{ user: IUser }>>(`${environment.urlApi}/account`),
          );
          const data = response.data?.user;
          if (!data) throw new Error('El API no devolvio el usuario.');
          const currentUser = new UserModel(data);
          this.currentUser.set(currentUser);
          return currentUser;
        } catch (error) {
          lastError = error;

          // Sin permiso: reintentar no lo va a conceder. Salida temprana.
          if (NO_RETRY_STATUS.has(statusOf(error))) break;

          if (attempt < MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
      }
      // Tanto en la salida temprana como al agotar los reintentos el estado
      // queda limpio: `currentUser()` null y `loggedIn()` false.
      this.currentUser.set(null);
      throw lastError;
    } finally {
      this.loading.set(false);
    }
  }

  async signOut(navigate = true): Promise<void> {
    try {
      await signOut(this.auth);
    } finally {
      this.currentUser.set(null);
      if (navigate) await this.router.navigate(['login'], { replaceUrl: true });
    }
  }
}
